/**
 * Debug Panel Component
 * Panel for debugging AST content
 */

import { Box, Button, Typography } from '@mui/material'
import React, { useState } from 'react'

import { ContentAST, astToLLMMarkdown } from '../../transforms/ast'

interface DebugPanelProps {
  ast: ContentAST
}

type DebugViewMode = 'ast' | 'markdown'

export const DebugPanel: React.FC<DebugPanelProps> = ({ ast }) => {
  const [viewMode, setViewMode] = useState<DebugViewMode>('ast')

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: '50%',
        bgcolor: 'var(--debug-bg)',
        borderLeft: '1px solid var(--debug-border)',
      }}
    >
      {/* Debug Header with Tab Navigation */}
      <Box
        sx={{
          borderBottom: '1px solid var(--debug-border)',
          py: 1,
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'var(--debug-bg)',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--debug-text)', fontFamily: 'monospace' }}>
          Debug View
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {(['ast', 'markdown'] as const).map((mode) => (
            <Button
              key={mode}
              size="small"
              onClick={() => setViewMode(mode)}
              sx={{
                fontSize: '11px',
                textTransform: 'none',
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                color: viewMode === mode ? 'primary.main' : 'text.secondary',
                bgcolor: viewMode === mode ? 'var(--debug-btn-bg)' : 'transparent',
                fontWeight: viewMode === mode ? 600 : 400,
                '&:hover': {
                  bgcolor: viewMode === mode ? 'var(--debug-btn-hover)' : 'var(--bg-hover)',
                },
              }}
            >
              {mode.toUpperCase()}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Debug Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: 'var(--bg-surface)',
        }}
      >
        {viewMode === 'ast' && <ASTView ast={ast} />}
        {viewMode === 'markdown' && <MarkdownView ast={ast} />}
      </Box>
    </Box>
  )
}

/**
 * AST View - Full AST JSON structure
 */
const ASTView: React.FC<{ ast: ContentAST }> = ({ ast }) => {
  return (
    <pre
      style={{
        margin: 0,
        padding: 0,
        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
        fontSize: '11px',
        lineHeight: '1.4',
        color: 'var(--debug-pre-text)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        direction: 'ltr',
        unicodeBidi: 'bidi-override', // Override any RTL content in JSON
        textAlign: 'left',
      }}
    >
      {JSON.stringify(ast, null, 2)}
    </pre>
  )
}

/**
 * Markdown View - AST rendered as LLM markdown
 */
const MarkdownView: React.FC<{ ast: ContentAST }> = ({ ast }) => {
  const markdown = astToLLMMarkdown(ast)
  
  return (
    <pre
      style={{
        margin: 0,
        padding: 0,
        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
        fontSize: '11px',
        lineHeight: '1.4',
        color: 'var(--debug-pre-text)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        direction: 'ltr',
        unicodeBidi: 'bidi-override',
        textAlign: 'left',
      }}
    >
      {markdown}
    </pre>
  )
}
